import { PublicClientApplication } from '@azure/msal-browser';
import { rateLimiters } from '../utils/rateLimiter';

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const DEFAULT_CLIENT_ID = "9509a59e-672c-42c3-af18-dcaa9e92d7ed";
const DEFAULT_AUTHORITY = "https://login.microsoftonline.com/f5da49bb-7f9e-41ee-82f3-0511ffc7f1a5/v2.0";
const DEFAULT_SCOPE = `api://${DEFAULT_CLIENT_ID}/access_as_user`;

let _pcaPromise = null;
async function getPca() {
  if (_pcaPromise) return _pcaPromise;
  _pcaPromise = (async () => {
    let cfg = null;
    try {
      const raw = sessionStorage.getItem('auth_config_cache');
      if (raw) cfg = JSON.parse(raw)?.data || null;
    } catch {}

    const msalConfig = {
      auth: {
        clientId: cfg?.clientId || DEFAULT_CLIENT_ID,
        authority: cfg?.authority || DEFAULT_AUTHORITY,
        redirectUri: cfg?.redirectUri || (window.location.origin + '/msal-redirect.html'),
        postLogoutRedirectUri: cfg?.postLogoutRedirectUri || (window.location.origin + '/msal-redirect.html'),
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    };

    const pca = new PublicClientApplication(msalConfig);
    await pca.initialize();

    try {
      const rawAcc = sessionStorage.getItem('msal.activeAccount');
      if (rawAcc) {
        const saved = JSON.parse(rawAcc);
        const match = pca.getAllAccounts().find(a => a.homeAccountId === saved?.homeAccountId) || pca.getAllAccounts()[0];
        if (match) pca.setActiveAccount(match);
      } else {
        const any = pca.getAllAccounts()[0];
        if (any) pca.setActiveAccount(any);
      }
    } catch {}

    return pca;
  })();
  return _pcaPromise;
}

async function tryRefreshAccessToken() {
  try {
    const pca = await getPca();
    const account = pca.getActiveAccount() || pca.getAllAccounts()[0];
    if (!account) return null;

    let apiScope = null;
    try {
      const raw = sessionStorage.getItem('auth_config_cache');
      apiScope = raw ? (JSON.parse(raw)?.data?.apiScope || null) : null;
    } catch {}

    const scopes = apiScope ? [apiScope] : [DEFAULT_SCOPE];
    const tokenResponse = await pca.acquireTokenSilent({
      scopes,
      account,
      forceRefresh: true,
    });
    return tokenResponse?.accessToken || null;
  } catch (e) {
    // Se precisar de interação (popup/redirect), não tenta aqui.
    return null;
  }
}

// 1) Interceptor global (override em window.fetch)
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  let [url, options = {}] = args;

  // Se for chamada relativa (ex: '/requisicoes'), prefixa com API_BASE
  if (typeof url === 'string' && url.startsWith('/')) {
    url = `${API_BASE}${url}`;
  }

  // 🚦 Rate Limiting - determina tipo baseado no método e URL
  if (typeof url === 'string' && url.startsWith(API_BASE)) {
    const method = (options.method || 'GET').toUpperCase();
    const rateLimitKey = `${method}:${url}`;
    
    // Determina qual rate limiter usar
    let limiter = rateLimiters.read; // Padrão
    
    if (url.includes('/upload') || url.includes('/pdf')) {
      limiter = rateLimiters.upload;
    } else if (url.includes('/aprovar') || url.includes('/finalizar')) {
      limiter = rateLimiters.critical;
    } else if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
      limiter = rateLimiters.write;
    } else if (url.includes('/search') || url.includes('/filtrar')) {
      limiter = rateLimiters.search;
    }
    
    // Verifica rate limit
    if (!limiter.consume(rateLimitKey)) {
      const status = limiter.check(rateLimitKey);
      console.warn(`⚠️ Rate limit: aguarde ${status.retryAfter}s`);
      
      // Retorna erro customizado
      return Promise.reject(new Error(`Rate limit excedido. Aguarde ${status.retryAfter} segundos.`));
    }
  }

  // Se for URL da nossa API, adiciona Authorization
  if (typeof url === 'string' && url.startsWith(API_BASE)) {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      };
    }
  }

  try {
    const response = await originalFetch(url, options);

    // Evita redirecionar durante o fluxo de autenticação para permitir mensagens claras
    const isAuthEndpoint = typeof url === 'string' && (
      url.includes('/auth/validate-token') || url.includes('/auth/config') || url.endsWith('/auth/health')
      // /grupos-ad/config é carregado pelo RoleContext; um 401 aqui NÃO deve limpar a
      // sessão nem redirecionar (evita loop de flicker na tela de login).
      || url.includes('/grupos-ad/config')
    );
    if (response.status === 401 && !isAuthEndpoint) {
      const alreadyRetried = !!options.__retry401;
      const isOurApi = typeof url === 'string' && url.startsWith(API_BASE);
      if (isOurApi && !alreadyRetried) {
        const fresh = await tryRefreshAccessToken();
        if (fresh) {
          sessionStorage.setItem('access_token', fresh);
          const nextOptions = {
            ...options,
            __retry401: true,
            headers: {
              ...options.headers,
              'Authorization': `Bearer ${fresh}`,
            },
          };
          return originalFetch(url, nextOptions);
        }
      }

      if (isOurApi) {
        // Fallback: sessão realmente inválida
        sessionStorage.clear();
        window.location.href = '/';
      }
    }

    return response;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// 2) Helper fino para JSON (NÃO coloca Authorization — já vem do interceptor)
export const authFetch = async (url, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  const body = options.body && typeof options.body !== 'string'
    ? JSON.stringify(options.body)
    : options.body;

  const res = await fetch(url, { ...options, headers, body }); // url pode ser '/requisicoes'
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  const contentType = res.headers.get('content-type') || '';
  return contentType.includes('application/json') ? res.json() : res.text();
};

// Normaliza header de qualquer requisição para garantir campos obrigatórios
function normalizeRequisicaoHeader(h) {
  if (!h) return h;
  if (!Array.isArray(h.anexos)) h.anexos = []; // 📎 Garante array de anexos

  // `imagens` e `imagem` são DERIVADOS de `anexos` — não são mais gravados.
  //
  // Antes o mesmo base64 era persistido três vezes (anexos + imagens + imagem),
  // triplicando o peso da linha no banco. Agora a escrita guarda só `anexos` e a
  // derivação acontece aqui, num ponto único, para que todas as telas continuem
  // lendo `header.imagens` como sempre.
  //
  // Requisições antigas seguem trazendo as colunas preenchidas: quando `anexos`
  // vier vazio, preservamos o que veio do banco.
  if (h.anexos.length > 0) {
    h.imagens = h.anexos
      .filter(a => a && typeof a.tipo === 'string' && a.tipo.startsWith('image/') && a.base64)
      .map(a => a.base64);
    if (!h.imagem) h.imagem = h.imagens[0] || '';
  } else if (!Array.isArray(h.imagens)) {
    h.imagens = h.imagem ? [h.imagem] : [];
  }
  // Converte snake_case para camelCase se necessário
  if (h.linkRef == null && h.link_ref != null) h.linkRef = h.link_ref;
  if (h.nivelPrioridade == null && h.nivel_prioridade != null) h.nivelPrioridade = h.nivel_prioridade;
  return h;
}

function normalizeRequisicao(r) {
  if (r && r.header) r.header = normalizeRequisicaoHeader(r.header);
  return r;
}

// Memoização curta + deduplicação in-flight para reduzir chamadas duplicadas (polling/troca de telas)
// Limite de 100 entradas para evitar crescimento irrestrito (memory leak)
const MEMO_MAX_SIZE = 100;
const _requestMemo = new Map();
// Geração do cache. `invalidateMemo` incrementa; toda requisição guarda a geração
// em que nasceu e só grava o resultado se ela ainda for a atual. Sem isso, uma
// busca que JÁ ESTAVA EM VOO quando o cache foi invalidado gravava a resposta
// velha por cima depois — com carimbo de hora novo. Era o que fazia um pedido
// recém-aprovado voltar para a fila de aprovações: o POST limpava o cache, a
// listagem anterior respondia em seguida com o pedido ainda pendente, e o
// refresh logo depois lia esse cache envenenado. Aparecia mais no celular, onde
// a resposta demora mais e a janela da corrida é maior.
let _memoEpoch = 0;

function _memoSet(key, value) {
  if (_requestMemo.size >= MEMO_MAX_SIZE && !_requestMemo.has(key)) {
    // Remove a entrada mais antiga
    const firstKey = _requestMemo.keys().next().value;
    _requestMemo.delete(firstKey);
  }
  _requestMemo.set(key, value);
}

async function memoizedJsonRequest(key, fetcher, ttlMs = 0) {
  const now = Date.now();
  const hit = _requestMemo.get(key);

  if (hit?.data !== undefined && (ttlMs > 0) && (now - hit.ts < ttlMs)) {
    return hit.data;
  }

  if (hit?.inFlight) {
    return hit.inFlight;
  }

  const epoch = _memoEpoch;
  const inFlight = (async () => {
    try {
      const data = await fetcher();
      // Só popula o cache se nada foi invalidado enquanto esta busca estava em
      // voo — senão uma resposta já vencida voltaria a valer por até um TTL
      // inteiro. Quem pediu ainda recebe o dado; o cache é que não guarda.
      if (epoch === _memoEpoch) {
        _memoSet(key, { data, ts: Date.now(), inFlight: null });
      } else {
        // Invalidado durante o voo: o dado nao entra no cache, mas o `inFlight`
        // PRECISA ser limpo. A entrada gravada antes da requisicao ficaria com a
        // promise pendurada e a leitura logo acima passaria a devolve-la para
        // sempre — cache permanentemente velho. Acontece quando o prefixo
        // invalidado e outro, porque a geracao e global e a chave nao foi apagada.
        const curr = _requestMemo.get(key);
        if (curr && curr.inFlight === inFlight) _memoSet(key, { ...curr, inFlight: null });
      }
      return data;
    } catch (err) {
      const curr = _requestMemo.get(key);
      if (curr) _memoSet(key, { ...curr, inFlight: null });
      throw err;
    }
  })();

  _memoSet(key, { data: hit?.data, ts: hit?.ts || 0, inFlight });
  return inFlight;
}

function invalidateMemo(prefixes = []) {
  // Invalida também o que está em voo (ver `_memoEpoch`): sem isso, apagar as
  // chaves aqui não impedia uma resposta antiga de repovoar o cache logo depois.
  _memoEpoch++;
  if (!Array.isArray(prefixes) || prefixes.length === 0) {
    _requestMemo.clear();
    return;
  }
  for (const k of Array.from(_requestMemo.keys())) {
    if (prefixes.some((p) => k.startsWith(p))) {
      _requestMemo.delete(k);
    }
  }
}

// 3) Serviços
export const requisicoesService = {
  listar: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const data = await authFetch(`/requisicoes${q ? `?${q}` : ''}`);
    return Array.isArray(data) ? data.map(normalizeRequisicao) : data;
  },
  obter: async (id) => {
    const data = await authFetch(`/requisicoes/${id}`);
    return normalizeRequisicao(data);
  },
  criar: async (dados) => {
    const out = await authFetch('/requisicoes', { method: 'POST', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },
  atualizar: async (id, dados) => {
    const out = await authFetch(`/requisicoes/${id}`, { method: 'PUT', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },
  historico: (id) => authFetch(`/requisicoes/${id}/historico`),
  // Reserva e devolve o proximo numero real da requisicao. Reaproveita a reserva
  // existente do mesmo usuario dentro de 2h, entao abrir a tela varias vezes nao
  // consome numeros novos.
  previewNumero: (emissao) => authFetch(
    `/requisicoes/preview-numero${emissao ? `?emissao=${encodeURIComponent(emissao)}` : ''}`,
    { method: 'POST' }
  ),
  resumo: (solicitante) => authFetch(`/requisicoes/resumo${solicitante ? `?solicitante=${encodeURIComponent(solicitante)}` : ''}`),
  listarLite: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const data = await authFetch(`/requisicoes/lite${q ? `?${q}` : ''}`);
    return Array.isArray(data) ? data.map(normalizeRequisicao) : data;
  },
};


export const aprovacoesService = {
  listarPendentes: () => memoizedJsonRequest('aprovacoes:pendentes', () => authFetch('/aprovacoes/pendentes'), 25000),
  listarPendentesLV2: () => memoizedJsonRequest('aprovacoes:pendentes:lv2', () => authFetch('/aprovacoes/pendentes/lv2'), 25000),  // 🆕 Novo endpoint
  aprovar: async (id, dados) => {
    const out = await authFetch(`/aprovacoes/${id}/aprovar`, { method: 'POST', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },
  aprovarLV2: async (id, dados) => {
    const out = await authFetch(`/aprovacoes/${id}/aprovar-lv2`, { method: 'POST', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },  // 🆕 Novo endpoint
  rejeitar: async (id, dados) => {
    const out = await authFetch(`/aprovacoes/${id}/rejeitar`, { method: 'POST', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },
  solicitarAlteracaoPoc: async (id, dados) => {
    const out = await authFetch(`/aprovacoes/${id}/solicitar-alteracao-poc`, { method: 'POST', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },  // 🆕 Solicita alteração na POC
  finalizar: async (id, dados) => {
    const out = await authFetch(`/aprovacoes/${id}/finalizar`, { method: 'POST', body: dados });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },
  liberarEdicaoPoc: async (id) => {
    const out = await authFetch(`/aprovacoes/${id}/liberar-edicao-poc`, { method: 'POST' });
    invalidateMemo(['aprovacoes:', 'compras:listar']);
    return out;
  },
};



export const comprasService = {
  // Listagem LEVE — use esta sempre que a tela não precisar da POC (`compra`).
  //
  // `compra` é criptografado como um blob único: o servidor tem de trazer o
  // texto cifrado inteiro do banco e decifrar linha por linha. Medido sobre a
  // base real (175 pedidos): a listagem com `compra` leva ~4s só no SQL e
  // move ~539MB; sem ela, 0,09s e 0,5MB. Como a poda não alcança o conteúdo
  // cifrado, a única forma de não pagar esse custo é não pedir a coluna.
  //
  // Traz header, status, histórico e itens — o suficiente para montar a lista.
  // Quem abre um pedido busca o registro completo por id.
  listarLite: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.dataInicio) queryParams.append('dataInicio', params.dataInicio);
    if (params.dataFim) queryParams.append('dataFim', params.dataFim);
    const queryString = queryParams.toString();
    const url = `/compras/lite${queryString ? `?${queryString}` : ''}`;
    const data = await memoizedJsonRequest(`compras:listar:lite:${queryString}`, () => authFetch(url), 15000);
    return Array.isArray(data) ? data.map(normalizeRequisicao) : data;
  },
  listar:   async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.dataInicio) queryParams.append('dataInicio', params.dataInicio);
    if (params.dataFim) queryParams.append('dataFim', params.dataFim);
    const queryString = queryParams.toString();
    const url = `/compras${queryString ? `?${queryString}` : ''}`;
    
    const data = await memoizedJsonRequest(`compras:listar:${queryString}`, () => authFetch(url), 15000);
    return Array.isArray(data) ? data.map(normalizeRequisicao) : data;
  },
  iniciar:  async (id) => {
    const out = await authFetch(`/compras/${id}/iniciar`, { method: 'POST' });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
  enviarAprovacao: async (id, dados) => {
    const out = await authFetch(`/compras/${id}/enviar-aprovacao`, {
      method: 'POST',
      body: JSON.stringify(dados),                    
      headers: { 'Content-Type': 'application/json' } 
    });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
  finalizar: async (id, dados) => {
    const out = await authFetch(`/compras/${id}/finalizar`, {
      method: 'POST',
      body: JSON.stringify(dados),                    
      headers: { 'Content-Type': 'application/json' } 
    });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
  salvarPocRascunho: async (id, dados) => {
    const out = await authFetch(`/compras/${id}/salvar-poc`, {
      method: 'POST',
      body: JSON.stringify(dados),
      headers: { 'Content-Type': 'application/json' }
    });
    invalidateMemo(['compras:listar']);
    return out;
  },
  solicitarEdicaoPoc: async (id) => {
    const out = await authFetch(`/compras/${id}/solicitar-edicao-poc`, { method: 'POST' });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
  salvarPdfPoc: async (id, dados) => {
    const out = await authFetch(`/compras/${id}/salvar-pdf-poc`, {
      method: 'POST',
      body: JSON.stringify(dados),
      headers: { 'Content-Type': 'application/json' }
    });
    invalidateMemo(['compras:listar']);
    return out;
  },
  reabrirEdicao: async (id) => {
    const out = await authFetch(`/compras/${id}/reabrir-edicao`, { method: 'POST' });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
  // 🆕 Compras registra uma aprovação obtida fora do sistema (pula o aprovador).
  // Exige motivo + comprovante; os níveis LV1/LV2 continuam valendo, então um pedido
  // acima do limite precisa de DUAS chamadas, uma para cada nível.
  aprovarDireto: async (id, dados) => {
    const out = await authFetch(`/compras/${id}/aprovar-direto`, {
      method: 'POST',
      body: JSON.stringify(dados),
      headers: { 'Content-Type': 'application/json' }
    });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
  // 🆕 Compras recusa o pedido. Exige motivo + comprovante.
  recusar: async (id, dados) => {
    const out = await authFetch(`/compras/${id}/recusar`, {
      method: 'POST',
      body: JSON.stringify(dados),
      headers: { 'Content-Type': 'application/json' }
    });
    invalidateMemo(['compras:listar', 'aprovacoes:']);
    return out;
  },
};

export const fornecedoresService = {
  listar: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.busca) queryParams.set('busca', params.busca);
    if (params.ativo !== undefined) queryParams.set('ativo', params.ativo);
    
    // Usar barra final para evitar redirects em alguns proxies/hosts
    const url = `/fornecedores/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return authFetch(url);
  },
  
  criar: (fornecedorData) => authFetch('/fornecedores/', {
    method: 'POST',
    body: fornecedorData
  }),
  
  obter: (id) => authFetch(`/fornecedores/${id}`),
  
  atualizar: async (id, updateData) => {
    // Backend atual não possui PUT /fornecedores/{id}; evita quebrar fluxo com 404.
    // Retorna um objeto compatível para telas que dependem do retorno imediato.
    const current = await authFetch(`/fornecedores/${id}`);
    return {
      ...current,
      ...(updateData || {}),
      id,
      _warning: 'Atualização persistente de fornecedor não disponível neste ambiente.'
    };
  },
  
  deletar: async (id) => {
    // Backend atual não possui DELETE /fornecedores/{id}; mantém resposta estável.
    return {
      success: false,
      id,
      _warning: 'Exclusão de fornecedor não disponível neste ambiente.'
    };
  },
  
  buscarPorCnpj: async (cnpj) => {
    // Backend atual não possui /fornecedores/buscar-por-cnpj/{cnpj}; reutiliza listagem com busca.
    const payload = await authFetch(`/fornecedores/?busca=${encodeURIComponent(cnpj || '')}&ativo=true`);
    const fornecedores = Array.isArray(payload) ? payload : (payload?.fornecedores || []);
    const onlyDigits = String(cnpj || '').replace(/\D/g, '');
    return fornecedores.find((f) => String(f?.cnpj || '').replace(/\D/g, '') === onlyDigits) || null;
  }
};